import {
  BackButton,
  Title,
  FormContainer,
  ImageSection,
  FormSection,
  BookImage,
  PhotoUploadSection,
  PhotoLabel,
  PhotoUploadButton,
  PublishButton,
  TitleField,
  EditionField,
  AuthorField,
  IsbnField,
  PriceField,
  DescriptionField,
  InputRow,
  ButtonContainer,
  ClearButton,
  SignInModal,
  ModalContent,
  ModalTitle,
  ModalText,
  ErrorMessage,
  ModalButtonContainer,
  CancelButton,
  ConfirmButton,
  StyledAutocomplete
} from './UserListings.styles.js';
import React, { useState, useRef, useEffect } from 'react';
import { 
  TextField, 
  Select, 
  MenuItem, 
  CircularProgress,
} from '@mui/material';
import ArrowLeftSharpIcon from '@mui/icons-material/ArrowLeftSharp';
import { handleSearch } from '../../Utils/search.ts';
import { useNavigate } from 'react-router-dom';


export default function UserListings() {
  const navigate = useNavigate();
  useEffect(() => {
    window.handleSearch = (searchValue) => {
      if (searchValue && searchValue.trim()) {
        navigate(`/listings?search=${encodeURIComponent(searchValue.trim())}`);
      }
    };
    return () => { window.handleSearch = undefined; };
  }, [navigate]);
  const [bookData, setBookData] = useState({
    title: '',
    author: '',
    format: 'Paperback',
    isbn: '',
    condition: 'Good',
    price: '',
    description: '',
    image: null,
    edition: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [errors, setErrors] = useState({
    title: false,
    author: false,
    price: false
  });
  const [formError, setFormError] = useState('');
  const [showClearModal, setShowClearModal] = useState(false);

  // Add a hidden file input reference
  const fileInputRef = useRef(null);

  const handleBookSelect = (event, book) => {
    if (book) {
      console.log('Selected book raw data:', book);
      console.log('Edition from selected book:', book.edition);
      
      setBookData(prevData => {
        const newData = {
          ...prevData,
          title: book.title,
          author: book.authors,
          isbn: book.isbn,
          format: book.format,
          image: book.thumbnail,
          edition: book.edition || ''
        };
        console.log('Final book data being set:', newData);
        return newData;
      });
    }
  };

  const handlePublish = () => {
    // First, reset any existing errors
    setErrors({
      title: false,
      author: false,
      price: false
    });
    setFormError('');

    // Then check for validation errors
    const newErrors = {
      title: !bookData.title.trim(),
      author: !bookData.author.trim(),
      price: !bookData.price
    };

    // If there are any validation errors, show them and stop
    if (Object.values(newErrors).some(error => error)) {
      setErrors(newErrors);
      setFormError('Please fill in all required fields: Title, Author, and Price');
      return;
    }

    // Only if all validations pass, check for sign in
    const isSignedIn = false;
    if (!isSignedIn) {
      navigate('/create-account');
      return;
    }

    console.log('Publishing:', bookData);
  };

  const handleClearClick = () => {
    // Check if any field has data
    const hasData = 
      bookData.title.trim() !== '' ||
      bookData.author.trim() !== '' ||
      bookData.isbn.trim() !== '' ||
      bookData.price !== '' ||
      bookData.description.trim() !== '' ||
      bookData.edition.trim() !== '' ||
      bookData.image !== null ||
      bookData.format !== 'Paperback' ||
      bookData.condition !== 'Good';

    // Only show the clear confirmation if there's data to clear
    if (hasData) {
      setShowClearModal(true);
    }
  };

  const handleConfirmClear = () => {
    setBookData({
      title: '',
      author: '',
      format: 'Paperback',
      isbn: '',
      condition: 'Good',
      price: '',
      description: '',
      image: null,
      edition: ''
    });
    setSearchResults([]);
    setShowClearModal(false);
  };

  // Add a function to handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setBookData(prev => ({
          ...prev,
          image: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Add a function to trigger file input click
  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  return (
    <div className="p-5 max-w-[1240px] mx-auto relative mt-[10px]">
      <BackButton onClick={() => window.history.back()}>
        <ArrowLeftSharpIcon fontSize='large'/> Back
      </BackButton>
      <Title>New Listing</Title>
      
      <FormContainer className="mobile-form-container">
        <ImageSection className="mobile-image-section">
          {bookData.image && (
            <BookImage className="mobile-book-image" src={bookData.image} alt="Book cover" />
          )}
          <PhotoUploadSection>
            <PhotoLabel>Add photo:</PhotoLabel>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              style={{ display: 'none' }}
            />
            <PhotoUploadButton onClick={handleUploadClick}>
              +
            </PhotoUploadButton>
          </PhotoUploadSection>
        </ImageSection>

        <FormSection>
          {formError && (
            <ErrorMessage>
              {formError}
            </ErrorMessage>
          )}
          <StyledAutocomplete
            freeSolo
            options={searchResults}
            loading={searching}
            getOptionLabel={(option) => 
              typeof option === 'string' ? option : `${option.title} by ${option.authors}`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Start typing your textbook title or ISBN..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searching ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            onInputChange={(event, value) => {
              handleSearch(value, setSearchResults, setSearching);
            }}
            onChange={handleBookSelect}
            renderOption={(props, option) => (
              <li {...props}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {option.thumbnail && (
                    <img 
                      src={option.thumbnail} 
                      alt={option.title} 
                      style={{ width: 40, height: 60, objectFit: 'cover' }}
                    />
                  )}
                  <div>
                    <div>{option.title}</div>
                    <div style={{ fontSize: '0.8em', color: '#666' }}>
                      by {option.authors}
                    </div>
                  </div>
                </div>
              </li>
            )}
          />

          <InputRow>
            <TitleField
              label="Title"
              value={bookData.title}
              onChange={(e) => setBookData({ ...bookData, title: e.target.value })}
              error={errors.title}
            />
            <EditionField
              label="Edition"
              value={bookData.edition}
              onChange={(e) => setBookData({ ...bookData, edition: e.target.value })}
            />
          </InputRow>

          <AuthorField
            label="Author(s)"
            value={bookData.author}
            onChange={(e) => setBookData({ ...bookData, author: e.target.value })}
            error={errors.author}
          />

          <Select
            fullWidth
            value={bookData.format}
            onChange={(e) => setBookData({ ...bookData, format: e.target.value })}
            sx={{ 
              height: '45px', 
              width: '130px',
              '& .MuiOutlinedInput-root': {
                height: '45px',
                borderRadius: '12px !important',
                backgroundColor: 'rgba(255, 255, 255, 0.9) !important',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(107, 137, 255, 0.2) !important',
                transition: 'all 0.3s ease',
                fontSize: '14px',
                '&:hover': {
                  borderColor: 'rgba(107, 137, 255, 0.4) !important',
                  boxShadow: '0 4px 15px rgba(107, 137, 255, 0.1)'
                },
                '&.Mui-focused': {
                  borderColor: '#6B89FF !important',
                  boxShadow: '0 4px 15px rgba(107, 137, 255, 0.2)'
                }
              },
              '& .MuiSelect-select': {
                height: '45px',
                display: 'flex',
                alignItems: 'center',
                padding: '0 15px !important',
                fontSize: '14px',
                textAlign: 'left'
              },
              '& .MuiInputLabel-root': {
                textAlign: 'left',
                left: '0px',
                top: '-10px',
                color: 'black',
                fontWeight: '500'
              },
              '& .MuiInputLabel-shrink': {
                transformOrigin: 'left',
                color: 'black'
              },
              '& .MuiFormHelperText-root': {
                position: 'absolute',
                bottom: '-20px',
                margin: '0',
                fontSize: '12px',
                height: '0'
              },
              marginBottom: '0 !important'
            }}
          >
            <MenuItem value="Hardcover">Hardcover</MenuItem>
            <MenuItem value="Paperback">Paperback</MenuItem>
          </Select>

          <IsbnField
            label="ISBN"
            value={bookData.isbn}
            onChange={(e) => setBookData({ ...bookData, isbn: e.target.value })}
          />

          <Select
            fullWidth
            value={bookData.condition}
            onChange={(e) => setBookData({ ...bookData, condition: e.target.value })}
            sx={{ 
              height: '45px', 
              width: '120px',
              '& .MuiOutlinedInput-root': {
                height: '45px',
                borderRadius: '12px !important',
                backgroundColor: 'rgba(255, 255, 255, 0.9) !important',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(107, 137, 255, 0.2) !important',
                transition: 'all 0.3s ease',
                fontSize: '14px',
                '&:hover': {
                  borderColor: 'rgba(107, 137, 255, 0.4) !important',
                  boxShadow: '0 4px 15px rgba(107, 137, 255, 0.1)'
                },
                '&.Mui-focused': {
                  borderColor: '#6B89FF !important',
                  boxShadow: '0 4px 15px rgba(107, 137, 255, 0.2)'
                }
              },
              '& .MuiSelect-select': {
                height: '45px',
                display: 'flex',
                alignItems: 'center',
                padding: '0 15px !important',
                fontSize: '14px',
                textAlign: 'left'
              },
              '& .MuiInputLabel-root': {
                textAlign: 'left',
                left: '0px',
                top: '-10px',
                color: 'black',
                fontWeight: '500'
              },
              '& .MuiInputLabel-shrink': {
                transformOrigin: 'left',
                color: 'black'
              },
              '& .MuiFormHelperText-root': {
                position: 'absolute',
                bottom: '-20px',
                margin: '0',
                fontSize: '12px',
                height: '0'
              },
              marginBottom: '0 !important'
            }}
          >
            <MenuItem value="Like New">Like New</MenuItem>
            <MenuItem value="Good">Good</MenuItem>
            <MenuItem value="Worn">Worn</MenuItem>
          </Select>

          <PriceField
            label="Price"
            type="number"
            value={bookData.price}
            onChange={(e) => setBookData({ ...bookData, price: e.target.value })}
            error={errors.price}
          />

          <DescriptionField
            multiline
            rows={4}
            label="Description (Optional)"
            value={bookData.description}
            onChange={(e) => setBookData({ ...bookData, description: e.target.value })}
          />

          <ButtonContainer>
            <ClearButton onClick={handleClearClick}>
              Clear
            </ClearButton>
            <PublishButton onClick={handlePublish}>
              Publish
            </PublishButton>
          </ButtonContainer>
        </FormSection>
      </FormContainer>

      {showClearModal && (
        <SignInModal>
          <ModalContent>
            <ModalTitle>Are you sure?</ModalTitle>
            <ModalText>
              This will clear all fields and remove any selected textbook.
            </ModalText>
            <ModalButtonContainer>
              <CancelButton onClick={() => setShowClearModal(false)}>
                Cancel
              </CancelButton>
              <ConfirmButton onClick={handleConfirmClear}>
                Clear All
              </ConfirmButton>
            </ModalButtonContainer>
          </ModalContent>
        </SignInModal>
      )}
    </div>
  );
}

